/**
 * agentMerge — merge a 3 vías para traer al canvas lo que hizo LMX (o el MCP)
 * sin pisar lo que el usuario tiene sin guardar.
 *
 *   base   = la última versión que este navegador cargó o guardó (currentProjectData)
 *   local  = lo que hay ahora en el canvas
 *   remote = lo que hay ahora en el servidor
 *
 * Por cada nodo/edge: si sólo cambió el servidor → gana el servidor; si sólo
 * cambió lo local → gana lo local; si cambiaron los dos de forma distinta → choque
 * (se devuelve conflict y el llamador cae al modal de siempre).
 * Sólo se comparan los campos que importan (tipo, posición redondeada, data
 * saneada, padre): lo que React Flow agrega al medir (width, height, selected…)
 * no cuenta como cambio.
 */
export const nodeKey = (sanitizeNodeData) => (n) => JSON.stringify({
  t: n.type,
  p: n.position ? { x: Math.round(n.position.x), y: Math.round(n.position.y) } : null,
  d: sanitizeNodeData ? sanitizeNodeData(n.data) : n.data,
  pa: n.parentId || n.parentNode || null,
});

export const edgeKey = (e) => JSON.stringify({
  s: e.source, sh: e.sourceHandle || null, t: e.target, th: e.targetHandle || null,
});

export const merge3 = (base = [], local = [], remote = [], key) => {
  const B = new Map(base.map((x) => [x.id, x]));
  const L = new Map(local.map((x) => [x.id, x]));
  const R = new Map(remote.map((x) => [x.id, x]));
  const ids = [...new Set([...L.keys(), ...R.keys(), ...B.keys()])];
  const items = [];
  const added = [];
  let conflict = false;
  ids.forEach((id) => {
    const b = B.get(id); const l = L.get(id); const r = R.get(id);
    const kb = b ? key(b) : null; const kl = l ? key(l) : null; const kr = r ? key(r) : null;
    const localChanged = kl !== kb;
    const remoteChanged = kr !== kb;
    if (!remoteChanged) { if (l) items.push(l); return; }       // el servidor no lo tocó
    if (!localChanged || kl === kr) {                           // sólo cambió el servidor
      if (r) {
        items.push(l ? { ...l, ...r } : r);                     // conserva selección/medidas locales
        if (!l) added.push(id);
      }
      return;
    }
    conflict = true;                                            // los dos lo cambiaron distinto
    if (l) items.push(l);
  });
  return { items, added, conflict };
};

// Envío LMX-6c: campos de RESULTADO de un nodo. Los escribe el servidor (el
// orquestador) o el propio nodo al terminar; no son una edición del usuario.
export const OUTPUT_KEYS = ['hasOutput', 'outputUrl', 'outputType', 'preview', 'assetId', 'processedAt'];

// Si el servidor tiene un resultado nuevo para un nodo (distinto del que este
// navegador cargó), se aplica encima de lo local sin tocar el resto del nodo.
export const applyServerOutputs = (items = [], base = [], remote = []) => {
  const B = new Map(base.map((n) => [n.id, n]));
  const R = new Map(remote.map((n) => [n.id, n]));
  return items.map((n) => {
    const r = R.get(n.id)?.data;
    if (!r?.outputUrl) return n;
    if (B.get(n.id)?.data?.outputUrl === r.outputUrl) return n;   // ya lo conocíamos
    if (n.data?.outputUrl === r.outputUrl && n.data?.hasOutput) return n;
    const out = {};
    OUTPUT_KEYS.forEach((k) => { if (k in r) out[k] = r[k]; });
    return { ...n, data: { ...(n.data || {}), ...out } };
  });
};
